import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Button } from 'shared/ui/button'
import {
  FileText,
  CheckSquare,
  BarChart3,
  Plus,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react'

interface DashboardState {
  user?: {
    name?: string
    role?: string
    department?: string
  }
  module?: 'E-PTW' | 'ADMIN'
  site?: {
    name?: string
  }
}

const DEFAULT_USER = {
  name: 'ผู้ใช้งาน',
  role: 'Permit Requester',
  department: 'Maintenance'
}

const DASHBOARD_STATS = {
  totalPermits: 156,
  pendingApproval: 12,
  activePermits: 28,
  completedToday: 8
}

export function DashboardPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state as DashboardState | undefined) ?? {}
  const currentUser = state.user ?? DEFAULT_USER

  useEffect(() => {
    if (!state.user) {
      navigate('/modules', { replace: true })
    }
  }, [state.user, navigate])

  const handleNavigate = (page: string) => {
    switch (page) {
      case 'permit-request':
        navigate('/permit-request', { state })
        break
      case 'permit-index':
        navigate('/permit-index', { state })
        break
      case 'approve-permit':
        navigate('/permit-approvals', { state })
        break
      default:
        console.info('Navigate to:', page)
    }
  }

  const handleCreateNew = () => {
    navigate('/permit-request')
  }

  const quickActions = [
    {
      title: 'สร้าง Permit ใหม่',
      description: 'เริ่มต้นสร้างเอกสารขออนุญาทำงาน',
      icon: Plus,
      action: handleCreateNew,
      variant: 'default' as const,
      show: ['Permit Requester'].includes(currentUser.role || '')
    },
    {
      title: 'ดู Permit Index',
      description: 'ดูรายการเอกสาร Permit ทั้งหมด',
      icon: FileText,
      action: () => handleNavigate('permit-index'),
      variant: 'outline' as const,
      show: true
    },
    {
      title: 'อนุมัติ Permit',
      description: 'ตรวจสอบและอนุมัติเอกสาร',
      icon: CheckSquare,
      action: () => handleNavigate('approve-permit'),
      variant: 'outline' as const,
      show: [
        'Safety Approver',
        'Area Owner',
        'Permit Controller',
        'Controller',
        'Shift Supervisor',
        'Permit Manager',
        'Permit VP',
      ].includes(currentUser.role || '')
    },
    {
      title: 'รายงาน',
      description: 'ดูรายงานสถิติและการวิเคราะห์',
      icon: BarChart3,
      action: () => handleNavigate('report'),
      variant: 'outline' as const,
      show: true
    }
  ]

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
        <h2 className="text-2xl font-semibold mb-2">
          ยินดีต้อนรับ, {currentUser.name}
        </h2>
        <p className="text-orange-100 mb-4">
          ระบบ Electronic Permit to Work (E-PTW) พร้อมให้บริการแล้ว
        </p>
        <p className="text-sm text-orange-100">
          บทบาท: {currentUser.role} • แผนก: {currentUser.department}
        </p>
        {state.site?.name ? (
          <p className="text-sm text-orange-100 mt-1">ไซต์งาน: {state.site.name}</p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Permits</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {DASHBOARD_STATS.totalPermits}
                </p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">รออนุมัติ</p>
                <p className="text-2xl font-semibold text-yellow-600">
                  {DASHBOARD_STATS.pendingApproval}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">กำลังดำเนินการ</p>
                <p className="text-2xl font-semibold text-green-600">
                  {DASHBOARD_STATS.activePermits}
                </p>
              </div>
              <Clock className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">เสร็จสิ้นวันนี้</p>
                <p className="text-2xl font-semibold text-blue-600">
                  {DASHBOARD_STATS.completedToday}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>การดำเนินการด่วน</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quickActions
              .filter((action) => action.show)
              .map((action, index) => {
                const Icon = action.icon
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <Icon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{action.title}</h4>
                        <p className="text-sm text-gray-500">{action.description}</p>
                      </div>
                    </div>
                    <Button variant={action.variant} size="sm" onClick={action.action}>
                      เลือก
                    </Button>
                  </div>
                )
              })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>กิจกรรมล่าสุด</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">PTW-20250104-0001 ได้รับการอนุมัติแล้ว</p>
                <p className="text-xs text-gray-500">2 ชั่วโมงที่แล้ว</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">PTW-20250104-0002 รอการอนุมัติจาก Safety</p>
                <p className="text-xs text-gray-500">4 ชั่วโมงที่แล้ว</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">PTW-20250104-0003 ถูกสร้างใหม่</p>
                <p className="text-xs text-gray-500">6 ชั่วโมงที่แล้ว</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
